// frontend/src/dashboardv2/hooks/useNewestMatch.js
//
// Implements the dashboardv2 hero pattern:
//   "Newest match in [your default saved search]"
// with the locked-in fallback chain:
//   1. Try the user's default saved search (isDefault: true)
//   2. If empty, try the next-most-recent saved search (by createdAt desc)
//   3. Continue until a saved search yields a match
//   4. If ALL saved searches are exhausted -> empty state
//
// Skip behavior:
//   - "Skip" advances to the next-newest profile in the SAME saved search
//   - When that search runs out, fall back to the next saved search in the chain
//
// Peers behavior:
//   - The hero shows the newest profile; `peers` exposes the remaining
//     profiles from the same batch for a "postage stamp" strip.
//
// All work uses the EXISTING /api/search endpoint (routes.py:5519) with:
//   sortBy=newest, sortOrder=desc, limit=BATCH_SIZE, page=N
// No new backend endpoints required.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import logger from '../../utils/logger';
import { buildDefaultCriteria } from '../../utils/searchDefaults';
import { isMembershipRequiredError } from '../../utils/membershipAccess';
import { searchProfilesStrict, fetchNewestMatch } from '../api';

// Number of profiles fetched per batch. The first becomes the hero pick;
// the rest are exposed as `peers` for the "postage stamp" strip.
const BATCH_SIZE = 11;

// Session snapshot: the last hero pick is hydrated instantly on the next
// page load while the fresh walk runs in the background — eliminates the
// skeleton entirely on repeat visits within the TTL.
const CACHE_TTL_MS = 10 * 60 * 1000;

function cacheKey() {
  const u = localStorage.getItem('username') || 'anon';
  return `dv2:newestMatch:${u}`;
}

function readCachedPick() {
  try {
    const raw = sessionStorage.getItem(cacheKey());
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d?.profile || Date.now() - (d.ts || 0) > CACHE_TTL_MS) return null;
    return d;
  } catch {
    return null;
  }
}

function writeCachedPick(pick, peers, position, hasMore) {
  try {
    if (!pick?.profile) {
      sessionStorage.removeItem(cacheKey());
      return;
    }
    sessionStorage.setItem(
      cacheKey(),
      JSON.stringify({
        ts: Date.now(),
        profile: pick.profile,
        savedSearch: pick.savedSearch || null,
        peers: peers || [],
        searchIndex: position?.searchIndex ?? 0,
        page: position?.page ?? 1,
        hasMore: !!hasMore,
      })
    );
  } catch {
    /* storage full/blocked — non-fatal */
  }
}

function toPositiveInt(value) {
  const n = Number.parseInt(String(value), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function heightToInches(feet, inches) {
  const f = toPositiveInt(feet);
  const i = toPositiveInt(inches) ?? 0;
  if (!f) return null;
  return f * 12 + i;
}

function coerceCriteriaForSearch(criteria, profileDefaults, currentUserProfile) {
  const copy = { ...(criteria || {}) };

  const userRole = currentUserProfile?.role?.toLowerCase();
  const isPrivileged = userRole === 'admin' || userRole === 'moderator';

  if (!isPrivileged && profileDefaults?.gender && copy.gender !== profileDefaults.gender) {
    copy.gender = profileDefaults.gender;
  }

  if (!copy.heightMin && (copy.heightMinFeet || copy.heightMinInches)) {
    const minInches = heightToInches(copy.heightMinFeet, copy.heightMinInches);
    if (minInches) copy.heightMin = String(minInches);
  }

  if (!copy.heightMax && (copy.heightMaxFeet || copy.heightMaxInches)) {
    const maxInches = heightToInches(copy.heightMaxFeet, copy.heightMaxInches);
    if (maxInches) copy.heightMax = String(maxInches);
  }

  return copy;
}

/**
 * Order saved searches for the fallback chain:
 *   default first, then by createdAt desc.
 */
function orderSavedSearches(savedSearches) {
  const list = [...(savedSearches || [])];
  return list.sort((a, b) => {
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;
    const aTime = new Date(a.createdAt || 0).getTime();
    const bTime = new Date(b.createdAt || 0).getTime();
    return bTime - aTime;
  });
}

/**
 * Try to find a hero pick by walking the saved-search list.
 * Returns { profile, peers, savedSearch, searchIndex, page, hasMore } or null.
 *
 * Bounded parallelism: while search[i] is in flight, search[i+1] is
 * prefetched — so a miss costs ~0 extra latency instead of a full RTT.
 * At most 2 searches are ever in flight (vs the old N-way parallel
 * fast-path), and the prefetch's rejection is swallowed so abandoning
 * it on a hit never logs an unhandled rejection.
 */
async function findNextPick(orderedSearches, startSearchIndex, startPage) {
  const runSearch = async (idx, page) => {
    const savedSearch = orderedSearches[idx];
    const data = await searchProfilesStrict(savedSearch.criteria || {}, {
      sortBy: 'newest',
      sortOrder: 'desc',
      limit: BATCH_SIZE,
      page,
      // Hero only needs "is there another page" — skip the full count scan.
      skipTotalCount: 'true',
    });
    return { data, savedSearch, searchIndex: idx, page };
  };

  const settled = (p) => p.then((res) => ({ res })).catch((err) => ({ err }));

  // Fire the next saved search while the current response is being
  // processed — a miss then costs ~0 extra latency instead of a serial RTT.
  // At most 2 requests are ever in flight (vs the old N-way parallel
  // fast-path), and an abandoned prefetch has a .catch attached so it can
  // never surface as an unhandled rejection.
  const prefetchNext = (idx) => {
    if (idx >= orderedSearches.length) return null;
    const promise = runSearch(idx, 1);
    promise.catch(() => {});
    return { idx, promise };
  };

  let lastError = null;
  let hadError = false;
  let inflight = null;

  for (let i = startSearchIndex; i < orderedSearches.length; i++) {
    const page = i === startSearchIndex ? startPage : 1;
    const currentPromise =
      inflight && inflight.idx === i ? inflight.promise : runSearch(i, page);
    inflight = prefetchNext(i + 1); // overlaps with current while it resolves
    const { res, err } = await settled(currentPromise);

    if (err) {
      hadError = true;
      lastError = err;
      // Abort the chain immediately on rate-limit or membership errors —
      // every saved search would fail identically, so walking them
      // just wastes requests before the lock teaser can render.
      if (err?.response?.status === 429 || isMembershipRequiredError(err)) {
        throw err;
      }
      logger.warn('findNextPick: search failed for', orderedSearches[i]?.name, err);
      continue;
    }

    const { data, savedSearch, searchIndex } = res;
    const results = data?.results || data?.users || [];
    const total = data?.total || 0;
    if (results.length > 0) {
      const offset = (page - 1) * BATCH_SIZE;
      const hasMore =
        typeof data?.hasMore === 'boolean'
          ? data.hasMore
          : offset + results.length < total;
      return {
        profile: results[0],
        peers: results.slice(1),
        savedSearch,
        searchIndex,
        page,
        hasMore,
      };
    }
    // Empty page → loop continues; the next request is already in flight.
  }

  if (hadError && lastError) {
    throw lastError;
  }

  return null;
}

/**
 * Hook entry point.
 *
 * @param {Array} savedSearches  full list from useDashboardData
 * @returns {{
 *   pick: { profile, savedSearch } | null,
 *   loading: boolean,
 *   error: any,
 *   isEmpty: boolean,
 *   skipPick: () => void,
 *   reload: () => void
 * }}
 */
export function useNewestMatch(savedSearches, currentUserProfile) {
  const orderedSearches = useMemo(
    () => orderSavedSearches(savedSearches),
    [savedSearches]
  );

  // Hydrate from the session snapshot once — the hero paints instantly and
  // the mount compute revalidates silently (loading-gate hides the flash).
  const [cachedSeed] = useState(readCachedPick);
  const [position, setPosition] = useState(
    cachedSeed
      ? { searchIndex: cachedSeed.searchIndex, page: cachedSeed.page }
      : { searchIndex: 0, page: 1 }
  );
  const [pick, setPick] = useState(
    cachedSeed
      ? { profile: cachedSeed.profile, savedSearch: cachedSeed.savedSearch }
      : null
  );
  const [peers, setPeers] = useState(cachedSeed?.peers || []);
  const [hasMore, setHasMore] = useState(!!cachedSeed?.hasMore);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEmpty, setIsEmpty] = useState(false);
  const requestIdRef = useRef(0);
  const inflightRef = useRef(false);
  const pendingRerunRef = useRef(false);
  const usedServerRef = useRef(false);

  const compute = useCallback(
    async (startIndex, startPage) => {
      const requestId = ++requestIdRef.current;
      inflightRef.current = true;
      usedServerRef.current = false;
      setLoading(true);
      setError(null);

      // Commit only if no newer compute() run has started since this one did.
      const commit = (next) => {
        if (requestIdRef.current !== requestId) return;
        if (next.pick !== undefined) {
          setPick(next.pick);
          // Snapshot for instant hydration on the next load. A null pick
          // (empty state) clears the snapshot so a stale hero can't linger.
          writeCachedPick(next.pick, next.peers, next.position, next.hasMore);
        }
        if (next.peers !== undefined) setPeers(next.peers);
        if (next.hasMore !== undefined) setHasMore(next.hasMore);
        if (next.position !== undefined) setPosition(next.position);
        if (next.isEmpty !== undefined) setIsEmpty(next.isEmpty);
      };

      try {
        // Preferred path — the backend walks the saved-search chain
        // server-side: one request regardless of chain depth, and it needs
        // neither `orderedSearches` nor `currentUserProfile`, so it can run
        // in parallel with stage-1 data loading. Fall through to the client
        // walk on 404 (older backend) or a server hiccup (5xx/network) so
        // the hero keeps working on any backend; auth/membership/rate
        // errors propagate to the lock teaser / error state as before.
        try {
          const res = await fetchNewestMatch(startIndex, startPage);
          // The server path is self-sufficient — a dep change during this
          // flight doesn't warrant a rerun (the result is already fresh).
          usedServerRef.current = true;
          if (res?.isEmpty || !res?.profile) {
            commit({
              pick: null,
              peers: [],
              hasMore: false,
              isEmpty: true,
              position: { searchIndex: startIndex, page: startPage },
            });
          } else {
            commit({
              pick: { profile: res.profile, savedSearch: res.savedSearch },
              peers: res.peers || [],
              hasMore: !!res.hasMore,
              position: {
                searchIndex: res.searchIndex ?? startIndex,
                page: res.page ?? startPage,
              },
              isEmpty: false,
            });
          }
          return;
        } catch (err) {
          const status = err?.response?.status;
          if (status === 429 || isMembershipRequiredError(err)) throw err;
          if (status && status !== 404 && status < 500) throw err;
          logger.warn(
            `newest-match endpoint unavailable (${status || 'network'}); falling back to client walk`
          );
        }

        if (!orderedSearches.length) {
          if (!currentUserProfile || Object.keys(currentUserProfile).length === 0) {
            commit({ pick: null, peers: [], hasMore: false, isEmpty: false });
            return;
          }

          const defaults = buildDefaultCriteria(currentUserProfile);
          const criteria = coerceCriteriaForSearch(
            {
              gender: defaults.gender,
              ageMin: defaults.ageMin,
              ageMax: defaults.ageMax,
              heightMin: heightToInches(defaults.heightMinFeet, defaults.heightMinInches)
                ? String(heightToInches(defaults.heightMinFeet, defaults.heightMinInches))
                : '',
              heightMax: heightToInches(defaults.heightMaxFeet, defaults.heightMaxInches)
                ? String(heightToInches(defaults.heightMaxFeet, defaults.heightMaxInches))
                : '',
              locations: defaults.locations,
              daysBack: defaults.daysBack,
            },
            defaults,
            currentUserProfile
          );

          const data = await searchProfilesStrict(criteria, {
            sortBy: 'newest',
            sortOrder: 'desc',
            limit: BATCH_SIZE,
            page: startPage,
            skipTotalCount: 'true',
          });
          const results = data?.results || data?.users || [];
          const total = data?.total || 0;
          if (results.length > 0) {
            const offset = (startPage - 1) * BATCH_SIZE;
            commit({
              pick: { profile: results[0], savedSearch: null },
              peers: results.slice(1),
              hasMore:
                typeof data?.hasMore === 'boolean'
                  ? data.hasMore
                  : offset + results.length < total,
              position: { searchIndex: 0, page: startPage },
              isEmpty: false,
            });
          } else {
            commit({ pick: null, peers: [], hasMore: false, isEmpty: true });
          }
          return;
        }

        const defaults = buildDefaultCriteria(currentUserProfile);
        const searchesWithOverrides = orderedSearches.map((s) => ({
          ...s,
          criteria: coerceCriteriaForSearch(s.criteria, defaults, currentUserProfile),
        }));

        // Sequential, default-first: walk saved searches in priority order and
        // stop at the first hit. Avoids firing one heavy /api/search aggregation
        // per saved search on every load (the old parallel fast-path did).
        const result = await findNextPick(searchesWithOverrides, startIndex, startPage);
        if (result) {
          commit({
            pick: { profile: result.profile, savedSearch: result.savedSearch },
            peers: result.peers || [],
            hasMore: result.hasMore || false,
            position: { searchIndex: result.searchIndex, page: result.page },
            isEmpty: false,
          });
        } else {
          commit({ pick: null, peers: [], hasMore: false, isEmpty: true });
        }
      } catch (err) {
        if (requestIdRef.current !== requestId) return;
        logger.error('useNewestMatch compute failed:', err);
        setError(err);
        setPick(null);
        setPeers([]);
        setHasMore(false);
        setIsEmpty(false);
      } finally {
        // Only the latest compute owns the flags — a canceled compute must
        // not clear inflight (a newer run may still be in flight) or consume
        // a pending rerun (the newer run already saw the landed deps).
        if (requestIdRef.current === requestId) {
          inflightRef.current = false;
          setLoading(false);
          // A dep change arrived mid-flight: rerun only when this compute
          // fell back to the client walk (it may have seen empty deps and
          // no-op'd). The server path already returned fresh data —
          // rerunning would just burn a duplicate request.
          if (pendingRerunRef.current) {
            pendingRerunRef.current = false;
            if (!usedServerRef.current) {
              computeRef.current?.(0, 1);
            }
          }
        }
      }
    },
    [currentUserProfile, orderedSearches]
  );

  // Always-fresh handle for the deferred rerun in compute's finally.
  const computeRef = useRef(compute);
  computeRef.current = compute;

  // Initial load + reload when saved searches change.
  // Fires on mount (not only once stage-1 data lands) — the server-side
  // newest-match walk needs neither dependency, so the hero resolves in
  // parallel with profile/saved-search loading. A dep change while a
  // compute is in flight must NOT restart it — the in-flight call already
  // reads fresh data server-side, and canceling it would only add a full
  // duplicate round trip to the hero wait. On the client-walk fallback
  // (older backend) the mount compute no-ops early, so by the time deps
  // land nothing is in flight and the rerun performs the real walk.
  useEffect(() => {
    if (inflightRef.current) {
      // Mark a rerun as pending — compute's finally decides whether it's
      // needed (client-walk fallback) or wasteful (server path succeeded).
      pendingRerunRef.current = true;
      return;
    }
    setPosition({ searchIndex: 0, page: 1 });
    compute(0, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedSearches, currentUserProfile]);

  // Skip = advance to next page within current saved search; fall through if needed
  const skipPick = useCallback(() => {
    compute(position.searchIndex, position.page + 1);
  }, [compute, position]);

  // Previous = go back one page within current saved search
  const previousPick = useCallback(() => {
    if (position.page > 1) {
      compute(position.searchIndex, position.page - 1);
    }
  }, [compute, position]);

  // Clicking a postage-stamp promotes that peer to the hero, swapping the
  // current hero back into the peer's slot (pure client-side, no refetch).
  const selectPeer = useCallback(
    (peer) => {
      if (!peer || !pick) return;
      const peerKey = peer?.username || peer?.profileId || peer?.id;
      setPeers(
        (peers || []).map((p) =>
          (p?.username || p?.profileId || p?.id) === peerKey ? pick.profile : p
        )
      );
      setPick({ profile: peer, savedSearch: pick.savedSearch });
    },
    [pick, peers]
  );

  // Reload from the top
  const reload = useCallback(() => {
    setPosition({ searchIndex: 0, page: 1 });
    compute(0, 1);
  }, [compute]);

  return {
    pick,
    peers,
    hasMore,
    position,
    loading,
    error,
    isEmpty,
    skipPick,
    previousPick,
    selectPeer,
    reload,
  };
}
